/**
 * 📊 Pilotage — tableau de bord métier (lot 1, 11/06/2026).
 * Réplique les KPI du xlsm du frangin (DASHBOARD / RAPPORT ANALYSE / RAPPORT STOCK).
 * Central only (§205) — le menu ne l'expose qu'à la centrale, l'API est gatée.
 */
import React, { useState, useEffect } from 'react';
import { RefreshCw, Target, Package, TrendingUp } from 'lucide-react';
import { API_URL } from '../../services/api';

const H = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });
const fmtN = (v) => Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
const fmtPct = (v) => v === null || v === undefined ? '—' : `${Math.round(v * 100)} %`;

const Tuile = ({ label, valeur, suffixe, accent }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-4 ${accent || ''}`}>
    <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{valeur}<span className="text-sm font-medium text-gray-400 ml-1">{suffixe}</span></div>
  </div>
);

export default function Pilotage() {
  const [kpi, setKpi] = useState(null);
  const [obj, setObj] = useState(null);
  const [reassort, setReassort] = useState(null);
  const [loading, setLoading] = useState(true);
  // Lancer un réassort (volet 2) : modale + boutiques
  const [boutiques, setBoutiques] = useState([]);
  const [lancement, setLancement] = useState(null); // {ligne, source, quantite, boutique_id, enCours}
  const [besoinsSeuls, setBesoinsSeuls] = useState(true); // toggle grille réassort

  useEffect(() => {
    fetch(`${API_URL}/boutiques`, { headers: H() })
      .then(r => r.json()).then(d => setBoutiques(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const ouvrirLancement = async (ligne) => {
    setLancement({ ligne, source: 'chargement', quantite: String(ligne.besoin_net), boutique_id: '', enCours: false });
    try {
      const r = await fetch(`${API_URL}/pilotage/reassort/source?reference=${encodeURIComponent(ligne.reference)}&couleur=${encodeURIComponent(ligne.couleur || '')}`, { headers: H() });
      const src = r.ok ? await r.json() : { indisponible: true };
      setLancement(l => (l && l.ligne === ligne ? { ...l, source: src } : l));
    } catch { setLancement(l => (l ? { ...l, source: { indisponible: true } } : l)); }
  };

  const validerLancement = async () => {
    if (!lancement || !lancement.source || lancement.source.indisponible) return;
    const qte = parseInt(lancement.quantite, 10);
    if (!qte || qte < 1) { alert('Quantité invalide'); return; }
    if (qte > 20 && !window.confirm(`⚠ ${qte} pièces d'un coup (cap conseillé : 20). Confirmer ?`)) return;
    if (!lancement.boutique_id) { alert('Choisissez la boutique de destination'); return; }
    if (lancement.ligne.en_fabrication > 0 &&
        !window.confirm(`⚠ ${lancement.ligne.en_fabrication} pièce(s) déjà en fabrication pour cette réf×couleur. Lancer quand même ${qte} de plus ?`)) return;
    setLancement(l => ({ ...l, enCours: true }));
    try {
      const r = await fetch(`${API_URL}/atelier/ordres-fabrication/${lancement.source.ordre_id}/relancer`, {
        method: 'POST',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantite: qte, couleur_or: lancement.ligne.couleur, boutique_id: parseInt(lancement.boutique_id, 10) }),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || 'Erreur lancement'); setLancement(l => ({ ...l, enCours: false })); return; }
      const numeros = (d.ordres_crees || []).map(o => o.numero_ordre || o).join(', ') || (d.numeros_serie || []).join(', ');
      alert(`✅ Réassort lancé : ${numeros || qte + ' OF créé(s)'}`);
      setLancement(null);
      load(); // la grille se met à jour : en fab +N, besoin_net baisse (anti-doublon)
    } catch { alert('Erreur réseau'); setLancement(l => ({ ...l, enCours: false })); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/pilotage/kpi`, { headers: H() }),
        fetch(`${API_URL}/pilotage/objectifs`, { headers: H() }),
        fetch(`${API_URL}/pilotage/reassort`, { headers: H() }),
      ]);
      if (r1.ok) setKpi(await r1.json());
      if (r2.ok) setObj(await r2.json());
      if (r3.ok) setReassort(await r3.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading && !kpi) return (
    <div className="flex items-center justify-center py-20"><RefreshCw className="h-8 w-8 animate-spin text-gray-300" /></div>
  );

  const lignes = (reassort?.grille || []).filter(g => !besoinsSeuls || (g.besoin_net ?? g.besoin) > 0);
  // groupement par collection (rempli après le raccordement volet A ; sinon « SANS COLLECTION »)
  const parCollection = lignes.reduce((acc, g) => {
    (acc[g.collection || 'SANS COLLECTION'] = acc[g.collection || 'SANS COLLECTION'] || []).push(g);
    return acc;
  }, {});

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📊 Pilotage</h1>
          <p className="text-sm text-gray-500">KPI métier — production, objectifs, réassort</p>
        </div>
        <button onClick={load} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Bloc 1 : cockpit ── */}
      {kpi && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Tuile label="FAB en cours" valeur={fmtN(kpi.production.fab_en_cours)} />
            <Tuile label="SAV en cours" valeur={fmtN(kpi.production.sav_en_cours)} />
            <Tuile label="FAB terminées (mois)" valeur={fmtN(kpi.production.fab_terminees_mois)} />
            <Tuile label="SAV terminés (mois)" valeur={fmtN(kpi.production.sav_termines_mois)} />
            <Tuile label="Durée moy. FAB" valeur={fmtN(kpi.production.duree_moy_fab_j)} suffixe="j" />
            <Tuile label="Durée moy. SAV" valeur={fmtN(kpi.production.duree_moy_sav_j)} suffixe="j" />
            <Tuile label="Articles en stock" valeur={fmtN(kpi.stock.en_stock)} />
            <Tuile label="Vendus" valeur={fmtN(kpi.stock.vendus)} />
            <Tuile label="Valeur stock (PVP)" valeur={fmtN(kpi.stock.valeur_stock_pvp)} suffixe="DH" />
            <Tuile label="Ancienneté moy. stock" valeur={fmtN(kpi.stock.anciennete_moy_j)} suffixe="j" />
            <Tuile label="Stock > 90 jours" valeur={fmtN(kpi.stock.stock_plus_90j)} accent="border-amber-200" />
            <Tuile label="Devis (convertis)" valeur={`${fmtN(kpi.devis.nb_convertis)}/${fmtN(kpi.devis.nb_total)}`} suffixe={kpi.devis.taux_conversion !== null ? fmtPct(kpi.devis.taux_conversion) : ''} />
            <Tuile label="CA net (mois)" valeur={fmtN(kpi.finances_mois.ca_net)} suffixe="DH" />
            <Tuile label="Encaissé (mois)" valeur={fmtN(kpi.finances_mois.encaisse)} suffixe="DH" />
            <Tuile label="En dépôt-vente" valeur={fmtN(kpi.depot_vente.en_depot)} suffixe={`· ${fmtN(kpi.depot_vente.valeur_pvp)} DH`} />
            <Tuile label="Dû aux déposants" valeur={fmtN(kpi.depot_vente.du_deposants)} suffixe="DH" />
          </div>
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-3 text-sm text-gray-500">
            🪙 Stock or théorique, PMP et allocation par mix : <strong>à venir</strong> — {kpi.stock_or.note}
          </div>
        </>
      )}

      {/* ── Bloc 2 : objectifs ── */}
      {obj && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-bold text-gray-700 flex items-center gap-2 mb-1">
            <Target className="h-4 w-4 text-gray-400" /> Objectifs {obj.annee} = réalisé {obj.reference} × {obj.coef_objectif}
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Réalisé {obj.reference} = pièces fabriquées l'an dernier · Réalisé {obj.annee} = fabriquées à ce jour ·
            Objectif = réalisé {obj.reference} × {obj.coef_objectif} (coefficient paramétrable) ·
            Avancement = réalisé {obj.annee} / objectif · <strong>Écart négatif = reste à faire</strong>.
          </p>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-500 border-b">
              <th className="text-left py-2">Type</th>
              <th className="text-right">Réalisé {obj.reference}</th>
              <th className="text-right">Réalisé {obj.annee}</th>
              <th className="text-right">Objectif {obj.annee}</th>
              <th className="text-right">Avancement</th>
              <th className="text-right">Écart (reste)</th>
              <th className="text-right">Poids moy. {obj.reference}</th>
              <th className="text-right">Métal {obj.annee}</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {obj.production_par_type.map(t => (
                <tr key={t.type}>
                  <td className="py-2 font-medium capitalize">{t.type}</td>
                  <td className="text-right">{t.nb_n1}</td>
                  <td className="text-right">{t.nb_ytd}</td>
                  <td className="text-right font-semibold">{t.objectif}</td>
                  <td className={`text-right ${t.avancement !== null && t.avancement >= 1 ? 'text-green-700 font-semibold' : ''}`}>{fmtPct(t.avancement)}</td>
                  <td className={`text-right ${t.ecart < 0 ? 'text-red-600' : 'text-green-700'}`}>{t.ecart > 0 ? '+' : ''}{t.ecart}</td>
                  <td className="text-right text-gray-500">{t.poids_moy_n1_g} g</td>
                  <td className="text-right text-gray-500">{t.metal_ytd_g} g</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              ⛓️ <strong>Chaîne</strong> : {obj.chaine_mousqueton.chaine.g_ytd} g consommés en {obj.annee} / objectif {obj.chaine_mousqueton.chaine.objectif_g} g
              ({obj.chaine_mousqueton.chaine.nb_ytd} pièces, contre {obj.chaine_mousqueton.chaine.nb_n1} en {obj.reference})
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              🔗 <strong>Mousqueton</strong> : {obj.chaine_mousqueton.mousqueton.g_ytd} g consommés en {obj.annee} / objectif {obj.chaine_mousqueton.mousqueton.objectif_g} g
              ({obj.chaine_mousqueton.mousqueton.nb_ytd} pièces, contre {obj.chaine_mousqueton.mousqueton.nb_n1} en {obj.reference})
            </div>
          </div>
          {obj.pierres_par_fournisseur?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">💎 Conso pierres par fournisseur (reconstruit — cassé dans le xlsm)</p>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 border-b">
                  <th className="text-left py-1.5">Fournisseur</th><th className="text-right">cts {obj.reference}</th>
                  <th className="text-right">cts {obj.annee}</th><th className="text-right">Objectif (cts)</th><th className="text-right">Nb pierres {obj.annee}</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {obj.pierres_par_fournisseur.map(f => (
                    <tr key={f.fournisseur}>
                      <td className="py-1.5">{f.fournisseur}</td>
                      <td className="text-right">{f.cts_n1}</td>
                      <td className="text-right">{f.cts_ytd}</td>
                      <td className="text-right font-semibold">{f.objectif_cts}</td>
                      <td className="text-right text-gray-500">{f.nb_ytd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modale Lancer réassort (volet 2) ── */}
      {lancement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
             onClick={() => !lancement.enCours && setLancement(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-1">🔨 Lancer un réassort</h3>
            <p className="text-sm text-gray-500 mb-3">
              <span className="font-mono font-semibold text-gray-700">{lancement.ligne.reference}</span>
              {' '}· <span className="capitalize">{(lancement.ligne.couleur || '').toLowerCase()}</span>
              {' '}· besoin net : <strong>{lancement.ligne.besoin_net}</strong>
              {lancement.ligne.en_fabrication > 0 && (
                <span className="text-blue-700"> · ⚠ {lancement.ligne.en_fabrication} déjà en fabrication</span>
              )}
            </p>
            {lancement.source === 'chargement' && <p className="text-sm text-gray-400">Recherche du modèle source…</p>}
            {lancement.source && lancement.source !== 'chargement' && lancement.source.indisponible && (
              <p className="text-sm text-red-600">{lancement.source.note || "Pas d'OF d'origine pour cette référence."}</p>
            )}
            {lancement.source && lancement.source !== 'chargement' && !lancement.source.indisponible && (
              <>
                <div className="bg-gray-50 rounded-lg p-3 text-sm mb-3">
                  <p className="font-medium text-gray-800">{lancement.source.nom || lancement.ligne.reference}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Modèle source : OF #{lancement.source.ordre_id}
                    {lancement.source.couleur_source && lancement.source.couleur_source !== lancement.ligne.couleur && (
                      <span className="text-amber-700"> (source en {lancement.source.couleur_source.toLowerCase()} → relancé en {(lancement.ligne.couleur || '').toLowerCase()})</span>
                    )}
                    {' '}· {lancement.source.prix != null ? `${fmtN(lancement.source.prix)} DH` : ''}
                    {lancement.source.nb_pierres > 0 && ` · ${lancement.source.nb_pierres} pierre(s) héritée(s) de l'OF source`}
                  </p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Quantité à lancer (cap conseillé : 20)</label>
                    <input type="number" min="1" value={lancement.quantite}
                      onChange={e => setLancement(l => ({ ...l, quantite: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400" autoFocus />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Boutique de destination</label>
                    <select value={lancement.boutique_id}
                      onChange={e => setLancement(l => ({ ...l, boutique_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                      <option value="">Choisir…</option>
                      {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setLancement(null)} disabled={lancement.enCours}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold disabled:opacity-50">Annuler</button>
              <button onClick={validerLancement}
                disabled={lancement.enCours || !lancement.source || lancement.source === 'chargement' || lancement.source.indisponible}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {lancement.enCours ? 'Lancement…' : 'Lancer le réassort'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bloc 3 : réassort ── */}
      {reassort && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-gray-700 flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-400" /> Réassort — cible {reassort.cible_par_ref_couleur} pièce(s) par référence × couleur
            </h3>
            <label className="text-xs text-gray-500 flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={besoinsSeuls} onChange={e => setBesoinsSeuls(e.target.checked)} />
              ⚠ besoins seulement
            </label>
          </div>
          <p className="text-xs text-gray-400 mb-3">{reassort.note}</p>

          {/* Synthèse « à produire » */}
          {reassort.a_produire && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                <div className="text-xs text-amber-700 uppercase font-semibold">À produire</div>
                <div className="text-xl font-bold text-amber-800">{reassort.a_produire.pieces} pièce(s)</div>
                <div className="text-xs text-amber-600">{reassort.a_produire.lignes} réf×couleur en manque</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                <div className="text-xs text-gray-500 uppercase font-semibold">Coût estimé (PMP connu)</div>
                <div className="text-xl font-bold text-gray-800">{fmtN(reassort.a_produire.cout_estime)} DH</div>
                <div className="text-xs text-gray-400">{reassort.a_produire.lignes_sans_pmp} ligne(s) sans PMP · {reassort.a_produire.note_pmp}</div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                <div className="text-xs text-gray-500 uppercase font-semibold">Valeur PVP du besoin</div>
                <div className="text-xl font-bold text-gray-800">
                  {fmtN((reassort.grille || []).reduce((s, g) => s + (g.besoin_net && g.pvp_moy ? g.besoin_net * g.pvp_moy : 0), 0))} DH
                </div>
                <div className="text-xs text-gray-400">potentiel de vente des pièces à produire</div>
              </div>
            </div>
          )}

          {/* Synthèse par collection */}
          {reassort.synthese_collections?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Synthèse par collection</p>
              <table className="w-full text-xs">
                <thead><tr className="text-gray-500 border-b">
                  <th className="text-left py-1.5">Collection</th><th className="text-right">Réfs</th>
                  <th className="text-right">En stock</th><th className="text-right">Vendus</th>
                  <th className="text-right">Besoin</th><th className="text-right">Valeur stock</th>
                  <th className="text-right">Taux rotation</th><th className="text-right">% complet</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {reassort.synthese_collections.map(s => (
                    <tr key={s.collection}>
                      <td className="py-1.5 font-medium">{s.collection}</td>
                      <td className="text-right">{s.nb_refs}</td>
                      <td className="text-right">{s.en_stock}</td>
                      <td className="text-right text-gray-500">{s.vendus}</td>
                      <td className={`text-right font-semibold ${s.besoin_net > 0 ? 'text-amber-700' : 'text-green-700'}`}>{s.besoin_net}</td>
                      <td className="text-right">{fmtN(s.valeur_stock)} DH</td>
                      <td className="text-right">{fmtPct(s.taux_rotation)}</td>
                      <td className="text-right">{fmtPct(s.pct_stock_complet)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lignes.length === 0 ? (
            <p className="text-sm text-green-700">✓ Toutes les cases série sont au niveau cible.</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="text-gray-500 border-b">
                <th className="text-left py-2">Référence</th><th className="text-left">Type</th><th className="text-left">Couleur</th>
                <th className="text-right">PMP</th><th className="text-right">PVP moy</th><th className="text-right">Marge %</th>
                <th className="text-right">Dern. vente</th>
                <th className="text-right">Stock</th><th className="text-right">Vendus</th>
                <th className="text-right">En fab</th><th className="text-center">Statut</th>
                <th className="text-right">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {Object.entries(parCollection).map(([coll, items]) => (
                  <React.Fragment key={coll}>
                    <tr className="bg-gray-50">
                      <td colSpan={12} className="py-1.5 px-1 font-bold text-gray-600">📁 {coll} <span className="font-normal text-gray-400">({items.length} ligne{items.length > 1 ? 's' : ''})</span></td>
                    </tr>
                    {items.map((g, i) => (
                      <tr key={`${coll}-${i}`}>
                        <td className="py-1.5 font-mono font-medium">{g.reference}</td>
                        <td className="capitalize text-gray-500">{(g.type || '').replace('_', ' ')}</td>
                        <td className="capitalize">{(g.couleur || '').toLowerCase()}</td>
                        <td className="text-right text-gray-500">{g.pmp != null ? fmtN(g.pmp) : '—'}</td>
                        <td className="text-right">{g.pvp_moy != null ? fmtN(g.pvp_moy) : '—'}</td>
                        <td className={`text-right ${g.marge_pct != null && g.marge_pct < 0.3 ? 'text-red-600' : 'text-gray-600'}`}>{fmtPct(g.marge_pct)}</td>
                        <td className="text-right text-gray-500">{g.derniere_vente ? new Date(g.derniere_vente).toLocaleDateString('fr-FR') : 'jamais'}</td>
                        <td className="text-right">{g.en_stock}</td>
                        <td className="text-right text-gray-500">{g.vendus}</td>
                        <td className={`text-right ${g.en_fabrication > 0 ? 'text-blue-700 font-semibold' : 'text-gray-400'}`}
                            title={g.date_lancement ? `Lancé le ${new Date(g.date_lancement).toLocaleDateString('fr-FR')} (${g.anciennete_fab_j} j)` : ''}>
                          {g.en_fabrication || 0}{g.anciennete_fab_j != null ? ` (${g.anciennete_fab_j} j)` : ''}
                        </td>
                        <td className="text-center">
                          {g.besoin_net > 0
                            ? <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">⚠ Besoin {g.besoin_net}</span>
                            : <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">✅ OK</span>}
                        </td>
                        <td className="text-right">
                          {g.besoin_net > 0 && g.a_source ? (
                            <button onClick={() => ouvrirLancement(g)}
                              className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold">
                              🔨 Lancer
                            </button>
                          ) : g.besoin_net > 0 ? (
                            <span className="text-gray-300" title="Aucun OF d'origine pour cette référence — création manuelle via l'Atelier">—</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
